import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLoader } from "@fortawesome/pro-regular-svg-icons";
import { useNavigate } from "react-router-dom";
import Section from "../../components/layout/Section/Section";
import { BaseText, BaseButton } from "../../components/ui";
import { PrimaryTable } from "../../components/ui/Tables";
import { useHostName, usePrivilegeCheck, useExportableData } from "../../hooks";
import { PageDataProvider } from "../../hooks/usePageDataContext";
import { DataTableContextProvider } from "../../components/ui/Tables/hooks/useDataTableContext";
import { getFontAwesomeIcon } from "../../utils";
import usePageListDetails from "../../hooks/usePageListDetails";

// TYPES
import {
	TableDisplayData,
	AdditionalData,
} from "../../components/ui/Tables/types";

const AdvanceShippingNoticePage: React.FC = () => {
	const hostName = useHostName();
	const { isAdmin } = usePrivilegeCheck();
	const navigate = useNavigate();
	const [pageTitle, setPageTitle] = useState("Advance Shipping Notices");
	const [_, setOrdersData, exportToCSV, isLoading] = useExportableData();
	const { pageMeta } = usePageListDetails("advance-shipping-notices-listing");
	const [hasModalTable, setHasModalTable] = useState<boolean>(true);
	const [shouldRenderResetFilters, setShouldRenderResetFilters] =
		useState(false);
	const [tableDisplayData, setTableDisplayData] = useState<TableDisplayData>(
		{} as TableDisplayData
	);
	const [callFromFilter, setCallFromFilter] = useState<boolean>(
		localStorage.getItem("approvalFilter") === "true"
	);
	const [additionalData, setAdditionalData] = useState<AdditionalData>(
		{} as AdditionalData
	);
	const [isAdditionalDataLoaded, setIsAdditionalDataLoaded] = useState(false);

	const slug = "advance-shipping-notices";
	const filterSlug = ["test-filter-slug"];

	const useApprovalFilter = () => {
		return localStorage.getItem("approvalFilter") === "true";
	};

	// Initialize the data for first load
	useEffect(() => {
		// Custom hook to check if approvalFilter is active
		const isApprovalFilterActive = useApprovalFilter();

		// Set initial additionalData based on the hostName and approvalFilter
		const initialAdditionalData: AdditionalData = {};

		// Set additionalData and mark it as loaded
		setAdditionalData(initialAdditionalData);
		setIsAdditionalDataLoaded(true);
	}, [hostName.hostName, isAdmin]);
	const handleFiltersChange = (isFilterActive: boolean) => {
		setShouldRenderResetFilters(isFilterActive);
	};

	const handleClearFilters = () => {
		localStorage.removeItem("searchCriteria");
		localStorage.removeItem("activeSortColumns");
		setShouldRenderResetFilters(false);
		navigate(0);
	};

	const handleCallFromFilter = () => {
		const isApprovalFilterActive = localStorage.getItem("approvalFilter");

		if (isApprovalFilterActive) {
			localStorage.removeItem("approvalFilter");
			setAdditionalData((prevData) => {
				const { salesOrderStageId, ...rest } = prevData;
				return rest;
			});
		} else {
			localStorage.setItem("approvalFilter", "true");
			setAdditionalData((prevData) => ({
				...prevData,
				salesOrderStageId: isAdmin ? [1, 10] : [10],
			}));
		}
		setCallFromFilter((prev) => !prev);
	};

	return (
		<div className=" px-[60px] h-full font-omnes-regular">
			<Section additionalClasses="flex justify-between py-6">
				<div className="flex items-center">
					{getFontAwesomeIcon("calculator", "solid")}
					<BaseText
						text={pageTitle}
						additionalClasses="pl-2"
						size="text-22px"
						color="text-navy-500"
						tag="h1"
						fontFamily="font-omnes-medium"
					/>
				</div>
			</Section>

			{/* Render PrimaryTable only if additionalData is loaded */}
			{isAdditionalDataLoaded && (
				<PageDataProvider pageMeta={pageMeta} pageTitle={pageTitle}>
					<DataTableContextProvider
						hasModalTable={false}
						data={[]}
						showStickyColumn={false}
					>
						<PrimaryTable
							filterSlug={filterSlug}
							callFromFilter={callFromFilter}
							setCallFromFilter={setCallFromFilter}
							pageTitle={""}
							hasModalTable={hasModalTable}
							setPageTitle={setPageTitle}
							setHasModalTable={setHasModalTable}
							slug={slug}
							setData={setOrdersData}
							additionalData={additionalData}
							onFiltersChange={handleFiltersChange}
							tableDisplayData={tableDisplayData}
						/>
					</DataTableContextProvider>
				</PageDataProvider>
			)}
		</div>
	);
};

export default AdvanceShippingNoticePage;
